import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Textarea } from "../ui/textarea";
import { AlertTriangle } from "lucide-react";

/** Replaces `window.prompt("Reason for reopening this month?")`. If payroll has already
 * calculated a line from this record (`review.payroll_line_status` is set), reopening is
 * blocked here in the UI before the user even tries - the backend enforces the same rule
 * (`timecard_service.reopen_month` raises 409) so this is a courtesy, not the only guard. */
export const ReopenMonthDialog = ({ open, onOpenChange, review, onSubmit }) => {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!review) return null;
  const payrollConsumed = !!review.payroll_line_status;

  const handleSubmit = async () => {
    if (!reason.trim()) return;
    setSubmitting(true);
    try {
      await onSubmit(reason.trim());
      setReason("");
      onOpenChange(false);
    } catch (e) {
      // Error already surfaced via toast by the parent action helper.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!submitting) { onOpenChange(next); if (!next) setReason(""); } }}>
      <DialogContent className="max-w-md" data-testid="reopen-month-dialog">
        <DialogHeader>
          <DialogTitle>Reopen Approved Timecard</DialogTitle>
          <DialogDescription>{review.employee_name} - {review.payroll_month}</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-2 text-xs bg-gray-50 rounded-lg p-2.5">
          <p className="text-gray-500">Current status</p><p className="text-right font-semibold text-gray-700">{review.official_record_status}</p>
          <p className="text-gray-500">Days recorded</p><p className="text-right font-semibold text-gray-700">{review.daily_records?.length || 0}</p>
          <p className="text-gray-500">Payroll status</p><p className="text-right font-semibold text-gray-700">{review.payroll_line_status || "Not yet calculated"}</p>
        </div>

        {payrollConsumed ? (
          <div className="flex items-start gap-2 text-xs bg-red-50 border border-red-200 text-red-700 rounded-lg p-3" data-testid="reopen-blocked-warning">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <span>Payroll has already calculated a line ({review.payroll_line_status}) from this record. Reopening now would silently invalidate that calculation - void or return the payroll line for {review.payroll_month} first, then reopen.</span>
          </div>
        ) : (
          <Textarea data-testid="reopen-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for reopening this month (required)" rows={3} />
        )}

        <DialogFooter>
          <button data-testid="reopen-dialog-cancel" onClick={() => onOpenChange(false)} disabled={submitting} className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-semibold hover:bg-gray-50">Close</button>
          {!payrollConsumed && (
            <button data-testid="reopen-dialog-confirm" disabled={submitting || !reason.trim()} onClick={handleSubmit} className="px-4 py-2 rounded-lg bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 disabled:opacity-50">
              {submitting ? "Reopening..." : "Reopen"}
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
